import { useEffect, useRef, useState } from 'react';
import type { AccountInScopeDto } from '@xcentral/api-client';
import { useAccountContext } from '../auth/useAccountContext';

/**
 * BT001 #16306 — the top-bar account context switcher.
 *
 * Renders ONLY for a caller whose own account has direct children. A caller with
 * nothing to switch between gets no control at all — not a disabled one, not a
 * one-item dropdown (BR-ACTX-002, AC-2).
 *
 * Choosing an account NAVIGATES (the URL is the context, per BR-ACTX-003); this
 * component writes no store. It renders the account NAME only — never the raw
 * GUID, AccountNumber, status, or any commercial field (BR-HW-003) — and it can
 * only ever list accounts the server returned.
 */
export function AccountSwitcher() {
  const { accountsInScope, activeAccountId, activeAccountName, ownAccountId, isParent, switchTo } =
    useAccountContext();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false);
    }
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        setOpen(false);
        buttonRef.current?.focus();
      }
    }
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  // Nothing to switch between means no control (AC-2).
  if (!isParent) return null;

  // The server orders own account first, then children by name. Do not re-sort —
  // partition in place so the own row keeps its position at the head.
  const ownAccount = accountsInScope.find((a) => a.id === ownAccountId);
  const childAccounts = accountsInScope.filter((a) => a.id !== ownAccountId);

  const label = activeAccountName || ownAccount?.name || 'Account';

  const renderItem = (account: AccountInScopeDto) => {
    const isActive = account.id === activeAccountId;
    return (
      <button
        key={account.id}
        type="button"
        role="menuitem"
        aria-current={isActive ? 'true' : undefined}
        onClick={() => {
          setOpen(false);
          if (account.id && !isActive) switchTo(account.id);
        }}
        className={`flex w-full items-center justify-between gap-2 px-4 py-2 text-left text-sm ${
          isActive ? 'bg-sand-50 font-medium text-fern-600' : 'text-gray-700 hover:bg-gray-50'
        }`}
      >
        <span className="truncate">{account.name}</span>
        {isActive && (
          <svg className="h-4 w-4 shrink-0 text-fern-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        )}
      </button>
    );
  };

  return (
    <div className="relative" ref={containerRef}>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Switch account context"
        className="flex max-w-[16rem] items-center gap-2 rounded-md border border-sand-200 px-3 py-1.5 text-sm hover:bg-sand-50"
      >
        <span className="text-[10px] uppercase tracking-[0.15em] text-sage-500">Account</span>
        {/* AC-7 — the active account name stays visible on every screen. */}
        <span className="truncate font-medium text-gray-800">{label}</span>
        <svg className="h-4 w-4 shrink-0 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <div
          role="menu"
          aria-label="Accounts"
          className="absolute left-0 z-50 mt-2 w-72 rounded-lg border border-sand-200 bg-white py-1 shadow-lg"
        >
          {ownAccount && (
            <>
              <p className="px-4 pt-2 pb-1 text-[10px] uppercase tracking-[0.15em] text-sage-500">Your account</p>
              {renderItem(ownAccount)}
            </>
          )}
          {childAccounts.length > 0 && (
            <>
              <p className="px-4 pt-2 pb-1 text-[10px] uppercase tracking-[0.15em] text-sage-500">Child accounts</p>
              {childAccounts.map(renderItem)}
            </>
          )}
        </div>
      )}
    </div>
  );
}
